import { CWBDrawerType } from '../../components/business/FinancialReportDrawer/FinancialReportDrawerConstants';
import { AssessPreCloudSections } from '../../pages/Dashboard/DashboardCover/DiscoverAssess/DiscoverAssessConstants';
import { IRowsData } from '../../pages/Migrate/DiscoverTab/EditableTable';
import { IMultipartFormData } from '../services/MultipartFormData.data';
import { ILandingZoneState } from '../store/state/BuildDeployState';
import { IWorkloadPattern } from './AppsAndSolutionsModels';
import { IAllEnvironmentsRespObj } from './BuildDeployModels';
import { ProjectStatus } from './OverviewModels';

export interface IAccountDetailsResponse {
  accountId: string;
  accountName: string;
  assetType: AssetType;
  projects: IProjectEntity[];
  pinned: boolean;
  status?: ProjectStatus;
}

export interface IProjectEntity {
  projectId: string;
  projectName: string;
  partner?: string;
  wbsCode?: string;
  applications?: string[];
  accessLevel?: string;
  createdOn?: string;
  createdBy?: string;
  status?: ProjectStatus;
}

export interface IAccountDetails {
  accountId: string;
  accountName: string;
  assetType: AssetType;
  projects: IProjectDetails[];
  pinned: boolean;
  status?: ProjectStatus;
}

export interface IProjectDetails {
  projectId: string;
  projectName: string;
  createdOn?: string;
  createdBy?: string;
  status?: ProjectStatus;

  // applications are the CWB modules that are enabled for a project
  // This data in incoming from CWB side
  applications?: string[];
}

export interface IProjectOwnersPayload {
  accountId: string | undefined;
  projectId: string | undefined;
  assetType: string | undefined;
}

export interface IProjectOwnersResponse {
  firstName: string;
  lastName: string;
  email: string;
}

export interface IAssessmentStateResponse {
  devsecops: boolean;
  governance: boolean;
  health: boolean;
  strategicGovernance: boolean;
  operationalGovernance: boolean;
  financialGovernance: boolean;
  devops: boolean;
  CloudFoundationStrategy: boolean;
}
export interface IAuditHistoryResponse {
  auditHistory?: IAuditHistory;
}

interface IAuditHistory {
  dashboard?: IAccountAuditHistory;
}

interface IAccountAuditHistory {
  account?: IAuditHistoryAccountDetails;
  project?: IDetails;
  enabledDiscoverAssessItems?: number[];
}

interface IDetails {
  id: string;
  name: string;
}

interface IAuditHistoryAccountDetails extends IDetails {
  assetType: AssetType;
}

export type IAuditHistoryPayload = IAuditHistory;

export interface IDiscoverAssessListItem {
  id: number;
  label: string;
  isEnabled: boolean;
  icon: string;
  popoverContent: string;
  cwbDrawerType: CWBDrawerType;
  reportCount: number;
  applicationName: string;
  isMoreOptions?: boolean;
  section?: AssessJourneySection;
  isDiscoverCard?: boolean;
  isCloudStrategy?: boolean;
  isFoundationSummaryCard?: boolean;
  preCloudSection?: AssessPreCloudSections;
}

export interface IConfigureWorkLoadsPattern {
  landingZoneName: string;
  uploadData: IConfigureWorkLoadPayload;
}

export interface IConfigureMicroServiceError {
  error: Error;
  landingZoneName: string;
}

export interface IConfigureWorkLoadPayload {
  userdata: IMultipartFormData;
  file: IMultipartFormData;
}

export interface ICheckLandingZoneStatus {
  landingZoneName: string;
  landingZoneId: string;
}

export interface ICheckMicroServicesPatternStatus {
  landingZoneName: string;
  microServiceId: string;
}

export enum AssetType {
  TANGRA = 'Tangra',
  CWB = 'CWB',
}

export enum AssessJourneySection {
  ANALYSIS = 'Assess',
  PRE_CLOUD = 'Discover',
  POST_CLOUD = 'Plan',
  DISCOVERY = 'Discovery',
  ASSESSMENT = 'Assessment',
  FOUNDATION_READINESS_ASSESSMENT = 'Foundation Readiness Assessment',
}

export enum SiteReliabilitySection {
  CHAOS_ENGINEERING = 'Chaos Engineering',
  SITE_RELIABILITY = 'Reliability Metrics',
  OBSERVABILITY = 'Observability',
}

export enum MigrateModernizeTabs {
  ENVIRONMENTS = 'Environments',
  WORKLOAD_PATTERNS = 'Workload Patterns',
  APPS_AND_SOLUTIONS = 'Apps and Solutions',
  PLATFORMS = 'Platforms',
  ASSESSMENT = 'Foundation Readiness Assessment',
}

export interface IDAQuestionnairItem {
  response: string;
  responseOption?: number;
  id: string;
  question: string;
}

export interface IDACapabilitySectionItem {
  capabilityName: string;
  questionnaire: IDAQuestionnairItem[];
}
export interface IDAQuestionnaireListItem {
  id: number;
  category: string;
  domain: string;
  isEnabled: boolean;
  popoverContent: string;
  isConfigured: boolean;
  isCloudStrategy: boolean;
  capabilities: IDACapabilitySectionItem[];
}

export interface IDAQuestionnairResponse {
  response: string;
  responseOption?: number;
  id: string;
}

export interface IDACapabilitySectionResponse {
  capabilityName: string;
  questionnaire: IDAQuestionnairResponse[];
}

export interface IDAQuestionnaireResponse {
  id: number;
  category: string;
  domain: string;
  isEnabled: boolean;
  popoverContent: string;
  isConfigured: boolean;
  isCloudStrategy: boolean;
  capabilities: IDACapabilitySectionResponse[];
}

// For Assess Tab
export interface ICSPAssessQuestionnairItem {
  response: string[];
  responseOption: string;
  id: string;
  question: string;
}
export interface ICSPAssessCapabilitySectionItem {
  capabilityName: string;
  questionnaire: ICSPAssessQuestionnairItem[];
  totalScore?: number;
}

export interface ICSPAssessQuestionnaireListItem {
  id: string;
  category: string;
  domain: string;
  isEnabled: boolean;
  popoverContent: string;
  isConfigured: boolean;
  capabilities: ICSPAssessCapabilitySectionItem[];
  domainTotalScore?: number;
}

export interface ICSPAssessQuestionnairResponseItem {
  responseOption: string;
  id: string;
}
export interface ICSPAssessCapabilitySectionResponseItem {
  capabilityName: string;
  questionnaire: ICSPAssessQuestionnairResponseItem[];
}
export interface ICSPAssessQuestionnaireResponseListItem {
  id: string;
  category: string;
  domain: string;
  isEnabled: boolean;
  popoverContent: string;
  isConfigured: boolean;
  capabilities: ICSPAssessCapabilitySectionResponseItem[];
}
export interface IPlanResponseItem {
  capabilityName: string;
  tableContent: IRowsData[];
}
export interface IPlanCardContent {
  id: string;
  category: string;
  domain: string;
  isEnabled: boolean;
  popoverContent: string;
  isConfigured: boolean;
  capabilities: IPlanResponseItem[];
}

export interface IPlanSaveCapabilityList {
  domain: string;
  capabilities: IPlanResponseItem[];
}

export interface ISelectedMigrateModernize {
  tabname: MigrateModernizeTabs;
  incomingObject?: ILandingZoneState | IAllEnvironmentsRespObj | IWorkloadPattern;
  isModalOpen: boolean;
}
